import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  displaySlotLabel,
  istDutyDate,
  postingLabel,
  readableDate,
  toTwelveHour,
  type OrderSheetDto,
  type OrderSheetDutyDto,
} from '@pams/shared';
import { ThemeScope } from '@/theme/ThemeScope';
import { api } from '@/lib/api';
import { shortfallHeadingOf, shortfallSentence } from '@/lib/shortfallNote';
import { presenceLine } from '@/lib/statusPresentation';
import { awaitedFrom } from '@/lib/commandWords';
import { queryKeys } from '@/lib/queryKeys';
import { PRINT } from './copy';

/**
 * ─── LAYER: page ─────────────────────────────────────────────────────────────────
 *
 *  [P6c §6] THE PRINTED ORDER SHEET.
 *
 *  ⚠️ IT DOES NOT MATCH A DEPARTMENTAL SAMPLE, AND THAT IS A CLIENT DECISION (closing
 *  OD-12). Duty points make the layout navigable BY POLICY: an order covering three places
 *  under two policies is three findable sections rather than one grid a reader has to scan.
 *
 *  ⚠️ IT RENDERS INSIDE `ThemeScope mode="light"`, AND WITHOUT THAT IT PRINTS BLANK.
 *  `applyThemeTokens` writes every colour token as an INLINE custom property on
 *  `document.documentElement` (`tokens.test.ts` asserts exactly that). An inline declaration
 *  on `<html>` cannot be overridden by any `@media print` rule without `!important` — and
 *  there is none in this repository — nor restated, which would put a second copy of the
 *  palette outside `tokens.ts` and break divergence #12. So an operator working in dark mode
 *  would send near-white text to a printer that drops backgrounds by default, and get a
 *  blank page. `ThemeScope` writes the LIGHT tokens inline on a nearer ancestor, which wins
 *  the cascade with no new colour value anywhere.
 *
 *  ⚠️ AND IT IS OUTSIDE `AppShell`. That shell is `h-screen overflow-hidden` with one
 *  scrolling `<main>` — §8's "THE WINDOW NEVER SCROLLS" — and `html, body { overflow: hidden }`
 *  CLIPS PRINT TO ONE PAGE in Chromium. `styles/print.css` undoes it for the print medium
 *  only; the bare route is what keeps the chrome off the paper in the first place.
 *
 *  ⚠️ NONE OF THAT IS VISIBLE TO VITEST. jsdom performs no layout, resolves no cascade and
 *  has no print medium, so the assertions that matter are in `e2e/specs/print.spec.ts` under
 *  `emulateMedia({ media: 'print' })`. What a unit test CAN see is the content, and
 *  `ArrangementPrint.test.tsx` asserts that.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ THE STORED LABEL, HUMANISED — NEVER A SECOND FORMATTER OVER `startsAt`/`endsAt`.
 *
 * `slot_label` is denormalised onto every duty at publish and already resolves the whole
 * question, including a duty with NO fixed time, which must never print `00:00–00:00`.
 * Formatting the two instants here would be a second expression of one sentence, and the two
 * would disagree first on exactly the midnight-spanning case that matters.
 * `humaniseTimes` is what makes rows written before the 12-hour change read like new ones.
 */
const dutyWindow = (duty: OrderSheetDutyDto): string => {
  /*
   * ⚠️ [C3a §6] `From 3:00 PM` WHERE THE DUTY HAS A REPORTING TIME AND NO SHIFT.
   *
   * `withReportingTime` AFTER `humaniseTimes` — the other order doubles the meridiem at noon.
   * The gate is `hasFixedTime`: every fixed-time duty carries a reporting time — its own start
   * since [C5b §3e] — so a value-gate would rewrite every ordinary duty on the sheet.
   *
   * ⚠️ THE `Report to:` LINE BELOW STILL STATES IT TOO, and that is accepted rather than tidied:
   * one answers "when does this duty run", the other "where do I assemble and when", and either
   * can exist without the other. [D4.2]'s "a heading over a labelled field" is a different shape.
   */
  /* ⚠️ [C5 §4a] ONE renderer — `humaniseTimes` order and the never-show-`No fixed time` rule
     are properties of `displaySlotLabel` now, not of four copies of this expression.
     [STAND-DOWN] It carries the stamped stand-down time, so the sheet prints `3:00 PM–9:00 PM`. */
  const label = displaySlotLabel({
    slotLabel: duty.slotLabel,
    hasFixedTime: duty.hasFixedTime,
    reportingTime: duty.reportingTime,
    standDownTime: duty.standDownTime,
  });
  return duty.spansMidnight ? `${label} ${PRINT.nextDay}` : label;
};

type SheetSource = OrderSheetDto['dutyPoints'][number]['duties'][number]['posts'][number]['sources'][number];

const OfficerList = ({ officers }: { officers: SheetSource['officers'] }) => {
  if (officers.length === 0) return <span className="text-muted-foreground">{PRINT.nobodyNamed}</span>;

  return (
    <ol className="m-0 list-decimal space-y-0.5 pl-5">
      {officers.map((officer) => (
        <li key={officer.id}>
          <span className="font-medium">{officer.name}</span>
          {officer.rankShortName === null ? null : (
            <span className="text-muted-foreground"> · {officer.rankShortName}</span>
          )}
          {/* ⚠️ [C5 §6] `Central Division · Nadial`. A printed order lists officers from every
              division that was asked, so a bare unit name leaves a reader working out which. */}
          {postingLabel(officer) === null ? null : (
            <span className="text-muted-foreground"> · {postingLabel(officer)}</span>
          )}
          {/*
            ⚠️ [C5 §7b] THE NUMBER, BENEATH THE NAME — a client decision that REVERSES a control
            `orderSheetOfficerSchema` held three ways (no field, a narrowed projection, and a
            payload scan). Its own header carries the argument and the cost; what this line owes
            is that the number is legible on paper, so it is its own block rather than a fourth
            ` · ` fragment on a line that already runs to the margin.
          */}
          {officer.phone === null ? null : (
            <span className="block font-mono text-xs text-muted-foreground">{officer.phone}</span>
          )}
        </li>
      ))}
    </ol>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §7a] ONE BLOCK PER SOURCE, WITH ITS OWN GAP BENEATH IT.
 *
 *  §7a: "WITHIN THE BLOCK, not collected at the end; a note fifteen rows away is a note nobody
 *  connects." Until C5 this sheet had no per-source structure at all — one flat list of names
 *  per post — and that was recorded as decided with the client (divergence 708: "the night's
 *  own table is the nearest block that exists"). That is what reverses here, and the sheet now
 *  carries the grain the requirement is actually written in.
 *
 *  ⚠️ THE GAP IS JOINED ON `allocKey`, NOT ON THE LABEL. Two sources can print the same words —
 *  a division-wide row and one of its stations resolve to different keys and can read alike
 *  after a rename — and `fillRef.util.ts`'s key is the identity. §7's identity-never-position
 *  rule, which this repository has corrected six times.
 *
 *  ⚠️ AND A SOURCE WITH NO GAP PRINTS NO LINE. A block reading `3 / 3` says everything already.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const SourceBlocks = ({
  post,
  shortfalls,
}: {
  post: OrderSheetDutyDto['posts'][number];
  shortfalls: OrderSheetDutyDto['shortfalls'];
}) => {
  if (post.sources.length === 0) return <span className="text-muted-foreground">{PRINT.nobodyNamed}</span>;

  return (
    <div className="space-y-1.5">
      {post.sources.map((source) => {
        const gaps = shortfalls.filter((note) => note.allocKey === source.allocKey);
        return (
          <div key={source.allocKey} className="break-inside-avoid" data-testid="print-source">
            <p className="m-0 text-xs font-semibold">
              {source.label}
              {/*
                ⚠️ THE BLOCK STATES ITS COUNT ONLY WHEN IT SAYS SOMETHING THE ROW DOES NOT.
                A post with ONE source has the same two numbers in its own Strength cell, and
                printing `1 / 2` twice on one row is the redundancy §4c names on the other
                surface. `ArrangementView`'s rule for the DP ordinal beside a derived title.
              */}
              {post.sources.length === 1 ? null : (
                <span className="ml-1 font-normal tabular-nums text-muted-foreground">
                  {source.namedCount} / {source.requiredCount}
                </span>
              )}
            </p>
            <OfficerList officers={source.officers} />
            {/*
              ⚠️ [C5c §6c] WHO TURNED UP, IN WORDS, DIRECTLY BENEATH THE NAMES IT COUNTS —
              `1 present · 1 absent · 0 not arrived`. The words are the meaning: a printed order is
              photocopied mono, where three tints are one grey, so the line wears NO tone and no pill.
              Beneath the names rather than the gap note, because it is about these people and the gap
              is about the ask.

              ⚠️ PRESENT ONLY WHEN THE SERVER SENT IT. The key is absent before the duty starts (§6d)
              and on a block naming nobody, and that is decided once, in `orderSheet.service.ts` — a
              second test here would be a second answer to "has this duty begun".
            */}
            {source.attendance === undefined ? null : (
              <p className="m-0 text-xs font-medium" data-testid="print-source-attendance">
                {presenceLine(source.attendance)}
              </p>
            )}
            {gaps.map((note) => (
              <p
                key={`${note.addresseeOrgUnitId}-${note.kind}`}
                className="m-0 text-xs text-muted-foreground"
                data-testid="print-source-gap"
                data-shortfall-kind={note.kind}
              >
                {shortfallHeadingOf(note.kind)}: {shortfallSentence(note)}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7A §3] A VEHICLE ROW'S OFFICERS CELL — WHERE IT IS REQUIRED FROM, AND THAT THERE IS NO MAPPING.
 *
 *  Never `OfficerList` (a van has no names, so it would print "Not yet named"), never an attendance
 *  line and NEVER A GAP. A short-fall note is keyed on an addressee's `allocKey`, and a van asked of
 *  Central shares Central's `CEN:*` with Central's UB — so `SourceBlocks` here would print UB's people
 *  gap a second time, under the van, as a vehicle short-fall. A source's figure is its required count
 *  alone, and only when several sources share the row (the row's own Strength cell says it otherwise).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const VehicleSources = ({ post }: { post: OrderSheetDutyDto['posts'][number] }) => (
  <div className="space-y-0.5">
    {post.sources.length === 0 ? null : (
      <p className="m-0 text-xs font-semibold" data-testid="print-vehicle-sources">
        {post.sources
          .map((source) =>
            post.sources.length === 1 ? source.label : `${source.label} · ${String(source.requiredCount)}`,
          )
          .join(', ')}
      </p>
    )}
    <p className="m-0 text-xs text-muted-foreground" data-testid="print-vehicle-no-mapping">
      {PRINT.vehicleNoMapping}
    </p>
  </div>
);

/**
 * ⚠️ [C5 §7a] NOTES WITH NO BLOCK TO SIT IN — see the note at the render site.
 *
 * A short-fall is keyed on an ALLOCATION row; a requirement edited after a division closed short
 * can leave one whose row no longer appears on any post of this duty. Those print under the
 * night's table, where every note used to, rather than vanishing.
 *
 * ⚠️ [P7A §3] "PRINTED" MEANS A PEOPLE BLOCK PRINTED IT. A vehicle row prints no gap (`VehicleSources`),
 * so its `allocKey` must not count — a people note sharing a van's key with no people block would
 * otherwise be treated as printed and vanish.
 */
const orphanedShortfalls = (duty: OrderSheetDutyDto): OrderSheetDutyDto['shortfalls'] => {
  const printed = new Set(
    duty.posts
      .filter((post) => post.kind !== 'vehicle')
      .flatMap((post) => post.sources.map((source) => source.allocKey)),
  );
  return duty.shortfalls.filter((note) => !printed.has(note.allocKey));
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a] ONE ROLE, ONE LINE — WHO HOLDS IT AND WHO STILL OWES IT, TOGETHER.
 *
 *  `awaitedCommand` travels as a separate array so `orderSheetCommandRowSchema`'s pinned keys did
 *  not move. Printed as a second list it would put a partly named In-charge on paper TWICE — once
 *  with its names and once "awaited" — and a reader counts two In-charges. So an awaited role is
 *  merged onto the named line of the SAME ROLE: `SI A. Roy · awaited from ND`. A role nobody has named
 *  yet prints a line of its own, `awaited from ND` — never `Not yet named`, which would not say who to
 *  ring.
 *
 *  ⚠️ [C6a review] "THE SAME ROLE" IS THE SERVICE'S `commandRow` — matched on the COMMAND KEY — AND
 *  NEVER THE LABEL. A card can hold a policy "Incharge" named by name and a one-off "Incharge" asked of
 *  CD: two roles, one word. Matched by label, the one-off's clause landed on the policy role's line and
 *  the one-off printed no line at all. A row with no `commandRow` has no named line and stands alone.
 *
 *  ⚠️ WORDS AND CODES, NEVER A NUMBER (FR-5.2.6b) — `awaitedFrom` lists distinct codes.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
type CommandLine = { key: string; label: string; names: string | null; awaited: string | null };

const commandLines = (duty: OrderSheetDutyDto): CommandLine[] => {
  const awaitedRows = duty.awaitedCommand ?? [];
  const claim = (index: number): string | null => {
    const row = awaitedRows.find((candidate) => candidate.commandRow === index);
    return row ? awaitedFrom(row.from) : null;
  };

  const named = duty.commands.map((row, index): CommandLine => {
    const awaited = claim(index);
    const names = row.officers
      .map((officer) => [officer.rankShortName, officer.name].filter(Boolean).join(' '))
      .join(', ');
    return {
      key: `${row.label}-${String(index)}`,
      label: row.label,
      /* An empty stamped row says so — unless the same line already says who owes it. */
      names: names !== '' ? names : awaited === null ? PRINT.nobodyNamed : null,
      awaited,
    };
  });
  const unclaimed = awaitedRows.filter((row) => row.commandRow === undefined);
  const awaitedOnly = unclaimed.map((row, index): CommandLine => ({
    key: `awaited-${row.label}-${String(index)}`,
    label: row.label,
    names: null,
    awaited: awaitedFrom(row.from),
  }));
  return [...named, ...awaitedOnly];
};

/** ⚠️ [C6a] Gated on the MERGED lines, so a duty whose only command is awaited still prints the block. */
const CommandBlock = ({ duty }: { duty: OrderSheetDutyDto }) => {
  const lines = commandLines(duty);
  return lines[0] === undefined ? null : (
    <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm" data-testid="print-command">
      {lines.map((line) => (
        <div key={line.key} className="contents">
          <dt className="font-semibold">{line.label}</dt>
          <dd className="m-0">
            {line.names}
            {line.names !== null && line.awaited !== null ? ' · ' : null}
            {line.awaited === null ? null : <span data-testid="print-command-awaited">{line.awaited}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const Duty = ({ duty, covered = false }: { duty: OrderSheetDutyDto; covered?: boolean }) => (
  <section className="break-inside-avoid border-border border-t pt-3" data-testid="print-duty">
    <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h4 className="m-0 text-base font-semibold">{readableDate(duty.dutyDate) ?? duty.dutyDate}</h4>
      <span className="text-muted-foreground text-sm">{dutyWindow(duty)}</span>
    </header>

    {/* ⚠️ [C6a] Command, named and awaited, in one block — see `CommandBlock`. */}
    <CommandBlock duty={duty} />

    {/*
      ⚠️ [C4a §3e] A DUTY WHOSE EVERY POST ASKS FOR NOTHING STILL PRINTS AS ITSELF. The service drops a
      post nobody asked for, so this duty can arrive with no posts at all — and a table with a header
      and no body reads as a sheet that failed to load. The date, the window and the command above
      still print; the force half says why it is empty.
    */}
    {/* ⚠️ [C5c §2] Not on a covered card — its force is the shared column, printed under its header. */}
    {duty.posts.length === 0 ? (
      covered ? null : (
        <p className="text-muted-foreground mt-3 mb-0 text-sm" data-testid="print-no-force">
          {PRINT.noForce}
        </p>
      )
    ) : (
      <table className="mt-3 w-full border-collapse text-sm">
        <thead>
          <tr className="border-border border-b text-left">
            <th className="w-[28%] py-1 pr-2 font-semibold">{PRINT.post}</th>
            <th className="w-[12%] py-1 pr-2 font-semibold">{PRINT.strength}</th>
            <th className="py-1 font-semibold">{PRINT.officers}</th>
          </tr>
        </thead>
        <tbody>
          {duty.posts.map((post, index) => (
            <tr
              key={`${post.label}-${String(index)}`}
              className="border-border/60 break-inside-avoid border-b align-top"
              data-post-kind={post.kind}
            >
              <td className="py-1.5 pr-2">
                {post.label}
                {post.sharedAcrossDutyPoints ? (
                  <span className="text-muted-foreground" data-testid="print-shared-post">
                    {' '}
                    {PRINT.coversEvery}
                  </span>
                ) : null}
              </td>
              {/* ⚠️ [P7A §3] A vehicle: `2 required` and where it is required from — no named count, no gap. */}
              {post.kind === 'vehicle' ? (
                <td className="py-1.5 pr-2 tabular-nums" data-testid="print-vehicle-required">
                  {PRINT.vehicleRequired(post.requiredCount)}
                </td>
              ) : (
                <td className="py-1.5 pr-2 tabular-nums">
                  {post.namedCount} / {post.requiredCount}
                </td>
              )}
              <td className="py-1.5">
                {post.kind === 'vehicle' ? (
                  <VehicleSources post={post} />
                ) : (
                  <SourceBlocks post={post} shortfalls={duty.shortfalls} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {/*
      ⚠️ [C4a §7b, amended C5 §7a] A SHORT-FALL SITS BENEATH THE REQUIREMENT IT QUALIFIES, AND THAT IS
      THE SOURCE'S OWN BLOCK NOW.

      [C4a §7b] put every note under the NIGHT's table, and said why it had to: "the sheet has no
      per-division blocks (each night is one table of post rows), so the night's own table is the
      nearest block that exists — decided with the client." §7a asks for the gap beneath each SOURCE,
      so `SourceBlocks` builds that grain and the notes moved into it, joined on `allocKey`.

      ⚠️ WHAT REMAINS HERE IS THE RESIDUE, AND IT MUST NOT BE DROPPED. A note whose allocation row is
      not on any post of this duty — a requirement edited after a division closed short — has no block
      to sit in, and a sheet that silently omitted it would under-report a shortage the operator was
      told about. It reads under the night's table exactly as every note used to.

      ⚠️ AND NOTHING PRINTS TWICE: this list is the complement of what the blocks rendered, computed
      from the same `allocKey` join rather than from a second rule.
    */}
    {orphanedShortfalls(duty).length === 0 ? null : (
      <div className="mt-2 break-inside-avoid" data-testid="print-shortfalls">
        <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
          {orphanedShortfalls(duty).map((note) => (
            <li key={`${note.addresseeOrgUnitId}-${note.kind}`} data-shortfall-kind={note.kind}>
              {shortfallHeadingOf(note.kind)}: {shortfallSentence(note)}
            </li>
          ))}
        </ul>
      </div>
    )}
  </section>
);

const ArrangementPrintPage = () => {
  const { arrangementId = '' } = useParams();

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.arrangements.orderSheet(arrangementId),
    queryFn: () => api.get<OrderSheetDto>(`/arrangements/${arrangementId}/order-sheet`),
    enabled: arrangementId.length > 0,
  });

  /**
   * ⚠️ THE TAB TITLE IS THE DEFAULT FILENAME when a browser prints to PDF, so an order
   * saved from here is named after itself rather than after the app. Restored on unmount:
   * this route is reachable by a normal in-app link, and a stale title would follow the
   * operator back to whatever they opened next.
   */
  useEffect(() => {
    if (!data) return undefined;
    const previous = document.title;
    /* ⚠️ [C2b §8b] The PA number leads when there is one — it is what a filed PDF is looked
       up by. `reference` stays, because it is the only identifier every order has. */
    document.title = data.paNumber
      ? `${data.paNumber} · ${data.reference} — ${data.displayTitle}`
      : `${data.reference} — ${data.displayTitle}`;
    return () => {
      document.title = previous;
    };
  }, [data]);

  return (
    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [amended C1c §6] THE SHEET SCROLLS ITSELF, AND THAT IS NOT DECORATION.
     *
     *  On screen this page showed exactly ONE VIEWPORT of the order and no scrollbar, while
     *  the printed output was complete. The cause is two facts meeting, and neither is a bug
     *  on its own: `styles/index.css` sets `html, body, #root { height: 100% }` and
     *  `html, body { overflow: hidden }` — "THE WINDOW NEVER SCROLLS", which is what stops an
     *  AntD popup appended to `<body>` growing the document — and `routes/index.tsx` puts this
     *  route OUTSIDE `AppShell`, deliberately, because the shell's chrome has no business on
     *  paper. So there was no `<main>`, and `<main>` is the app's only scroll container. The
     *  sheet grew past a viewport-tall `#root` and the excess was simply clipped.
     *
     *  ⚠️ THE FIX IS SCOPED HERE AND MUST NOT RELAX THE GLOBAL RULE. `print.spec.ts` asserts
     *  that `document.body`'s computed overflow is `hidden` ON SCREEN — it pins the clip as
     *  correct — so touching `index.css` would turn that test red for the wrong reason. A
     *  scroller of this page's own leaves it green, which is the guard working.
     *
     *  ⚠️ AND IT IS UNDONE UNDER `print`. `print.css` already restores `height: auto` and
     *  `overflow: visible` on the root three; a fixed-height scroller here would paginate to
     *  one page again, which is the very failure that file's header records.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    <ThemeScope mode="light" className="h-screen overflow-y-auto print:h-auto print:min-h-0 print:overflow-visible">
      <div className="mx-auto max-w-[900px] px-8 py-10 print:px-0 print:py-0" data-testid="order-sheet">
        {/* ⚠️ `print:hidden`, never conditional rendering — the button must exist for a
            keyboard user and must not exist on the paper. */}
        <div className="mb-6 flex justify-end gap-2 print:hidden">
          <button
            type="button"
            className="border-border rounded-md border px-3 py-1.5 text-sm font-medium"
            onClick={() => {
              window.print();
            }}
          >
            {PRINT.print}
          </button>
        </div>

        {isPending ? <p role="status">{PRINT.loading}</p> : null}
        {isError ? <p role="alert">{PRINT.failed}</p> : null}

        {data ? (
          <article className="space-y-6">
            <header className="border-border border-b pb-4">
              <p className="text-muted-foreground m-0 text-xs tracking-wide uppercase">{PRINT.masthead}</p>
              <h1 className="mt-1 mb-0 text-2xl font-bold">{data.displayTitle}</h1>
              <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="font-semibold">{PRINT.reference}</dt>
                <dd className="m-0 tabular-nums">{data.reference}</dd>
                {/*
                  ⚠️ [C2b §8b] THE OPERATOR'S OWN NUMBER, AND THE ROW IS OMITTED WHEN THERE IS
                  NONE — `estimatedCrowdSize` one row down is the precedent. A labelled empty
                  row on a printed order reads as a field somebody forgot to fill in.
                */}
                {data.paNumber === null ? null : (
                  <>
                    <dt className="font-semibold">{PRINT.paNumber}</dt>
                    <dd className="m-0 tabular-nums">{data.paNumber}</dd>
                  </>
                )}
                {/*
                  ⚠️ [C2b §4f] A CORRIGENDUM SAYS WHAT IT CORRECTS, ON THE PAPER. Two sheets for
                  one night with nothing linking them is the failure a corrigendum exists to
                  avoid — and the sheet is the only artefact that leaves this system.
                */}
                {data.correctsReference === null ? null : (
                  <>
                    <dt className="font-semibold">{PRINT.corrects}</dt>
                    <dd className="m-0 tabular-nums" data-testid="print-corrects">
                      {data.correctsReference}
                    </dd>
                  </>
                )}
                <dt className="font-semibold">{PRINT.orderDate}</dt>
                <dd className="m-0">{readableDate(data.paDate) ?? data.paDate}</dd>
                {data.estimatedCrowdSize === null ? null : (
                  <>
                    <dt className="font-semibold">{PRINT.crowd}</dt>
                    <dd className="m-0">{data.estimatedCrowdSize}</dd>
                  </>
                )}
              </dl>
              {data.description === null ? null : <p className="mt-3 mb-0 text-sm">{data.description}</p>}
            </header>

            {data.dutyPoints.map((point) => (
              <section key={point.id} className="space-y-3" data-testid="print-duty-point">
                <div className="border-border/60 border-b pb-2">
                  <h2 className="m-0 text-lg font-bold">
                    {point.ordinal}. {point.title}
                  </h2>
                  {/*
                   * ⚠️ [amended C1c §1a, §1d] THE PLACE AND THE TIME ARE INDEPENDENT, so the
                   * line renders for either. A card carrying only a reporting time used to
                   * print nothing — "Report by 7:00 AM" is a complete instruction when
                   * everybody already knows where.
                   */}
                  {point.reportingPlace === null && point.reportingTime === null ? null : (
                    <p className="text-muted-foreground m-0 text-sm">
                      {PRINT.reportTo}:{' '}
                      {[
                        point.reportingPlace?.name,
                        point.reportingTime === null ? null : toTwelveHour(point.reportingTime),
                      ]
                        .filter((part): part is string => Boolean(part))
                        .join(' · ')}
                    </p>
                  )}
                  {/* [STAND-DOWN] The card's own stand-down time, its own line — either may exist alone. */}
                  {point.standDownTime === null ? null : (
                    <p className="text-muted-foreground m-0 text-sm">
                      {PRINT.standDown}: {toTwelveHour(point.standDownTime)}
                    </p>
                  )}
                  {/* [C4a §4e] The card's zone, its own line — and no labelled empty row when unset or absent. */}
                  {(point.zone ?? '') === '' ? null : (
                    <p className="text-muted-foreground m-0 text-sm" data-testid="print-zone">
                      {PRINT.zone}: {point.zone}
                    </p>
                  )}
                  {/*
                    ⚠️ [C5c §4] The card's sector, its own line beneath the zone — WHOLE, and wrapped
                    rather than clipped: it has no length limit, and paper has no hover to read the rest.
                    `break-words` also keeps an unbroken run from pushing the sheet sideways.
                  */}
                  {(point.sector ?? '') === '' ? null : (
                    <p className="text-muted-foreground m-0 text-sm break-words" data-testid="print-sector">
                      {PRINT.sector}: {point.sector}
                    </p>
                  )}
                  {/* [C5c §2] Each column this card is covered for, and where its people are named. */}
                  {(point.coveredBy ?? []).map((row) => (
                    <p key={row.label} className="m-0 text-sm" data-testid="print-covered-by">
                      {PRINT.sharedFrom(row.label, row.anchorOrdinal, row.anchorTitle)}
                    </p>
                  ))}
                </div>

                {point.duties.map((duty) => (
                  <Duty key={duty.deploymentId} duty={duty} covered={(point.coveredBy ?? []).length > 0} />
                ))}

                {point.instructions.length === 0 ? null : (
                  <div className="break-inside-avoid">
                    <h3 className="mt-3 mb-1 text-sm font-semibold">{PRINT.pointInstructions}</h3>
                    <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
                      {point.instructions.map((line, index) => (
                        <li key={`${point.id}-i-${String(index)}`}>{line.text}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {point.supportingArrangements.length === 0 ? null : (
                  <div className="break-inside-avoid">
                    <h3 className="mt-3 mb-1 text-sm font-semibold">{PRINT.supporting}</h3>
                    <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
                      {point.supportingArrangements.map((entry, index) => (
                        <li key={`${point.id}-s-${String(index)}`}>
                          <span className="font-medium">{entry.personName}</span>
                          {entry.responsibilities.length === 0 ? null : ` — ${entry.responsibilities.join('; ')}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            ))}

            {/* ⚠️ [D4.3] PA-level instructions print BELOW the points, because they qualify
                every one of them. A duty point's own sit inside its section. */}
            {data.instructions.length === 0 ? null : (
              <section
                className="break-inside-avoid border-border border-t pt-4"
                data-testid="print-order-instructions"
              >
                <h2 className="mt-0 mb-1 text-base font-bold">{PRINT.orderInstructions}</h2>
                <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
                  {data.instructions.map((line, index) => (
                    <li key={`o-i-${String(index)}`}>{line.text}</li>
                  ))}
                </ul>
              </section>
            )}

            {data.supportingArrangements.length === 0 ? null : (
              <section className="break-inside-avoid">
                <h2 className="mt-0 mb-1 text-base font-bold">{PRINT.supporting}</h2>
                <ul className="m-0 list-disc space-y-0.5 pl-5 text-sm">
                  {data.supportingArrangements.map((entry, index) => (
                    <li key={`o-s-${String(index)}`}>
                      <span className="font-medium">{entry.personName}</span>
                      {entry.responsibilities.length === 0 ? null : ` — ${entry.responsibilities.join('; ')}`}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <footer className="text-muted-foreground border-border border-t pt-3 text-xs">
              {/* ⚠️ [amended C5c §6] THE IST DAY OF THE INSTANT, NEVER `.slice(0, 10)` ON THE ISO STRING —
                  that is the UTC day, and a sheet printed 00:00–05:30 IST was dated YESTERDAY
                  ([IST-FIX]'s defect; `istBusinessDate.test.ts` does not scan `web/src`). */}
              {PRINT.generated} {readableDate(istDutyDate(data.generatedAt)) ?? istDutyDate(data.generatedAt)}
            </footer>
          </article>
        ) : null}
      </div>
    </ThemeScope>
  );
};

export default ArrangementPrintPage;
